import { useEffect, useRef, useState } from "react";
import { api, API_BASE, getToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Database, Upload, RefreshCw, CheckCircle2, AlertTriangle, FileText, Landmark, Download } from "lucide-react";

const KINDS = [
  {
    key: "products",
    endpoint: "/sap/sync/products",
    template: "/templates/products_template.csv",
    title: "Products (Item Master)",
    icon: FileText,
    hint: "Custom columns: Item No., Item Description, Category, Sub Category, Color, Storage, MRP, Image URL, Description, Active",
  },
  {
    key: "price-lists",
    endpoint: "/sap/sync/price-lists",
    template: "/templates/price_lists_template.csv",
    title: "Price Lists",
    icon: Landmark,
    hint: "Direct SAP export: Group Name, Item No., Base Price List, Factor, Primary Currency - Base Price, Primary Currency - Price, Primary Currency - Manual, Inventory UoM, Pricing Unit, POSSync, POSReqId, CurrentKey",
  },
  {
    key: "dealers",
    endpoint: "/sap/sync/dealers",
    template: "/templates/dealers_template.csv",
    title: "Dealers & Addresses",
    icon: Landmark,
    hint: "SAP BP + Address export: BP Code, BP Name, Group Name, Group No., Inactive, Address_Type, Address_Name/ID, Street, Block, City, Zip Code, GSTIN, Is_Default. One row per address — a BP Code can repeat across multiple rows for multiple billing/shipping addresses. Also sets the pricing group mapping from Group Name. Note: Area isn't in this file, so assign each dealer's Area afterward from the Dealers page before a manager can be scoped to them.",
  },
  {
    key: "dealer-map",
    endpoint: "/sap/sync/dealer-map",
    template: "/templates/dealer_map_template.csv",
    title: "Dealer → Group Map",
    icon: Landmark,
    hint: "Just two columns: CardCode, Group Name. Use this if you only need to (re)assign which price-list group a dealer belongs to, without re-uploading the full BP + Address export. CardCode must match an existing dealer's code, and Group Name must exactly match a price list's Group Name.",
  },
  {
    key: "outstanding",
    endpoint: "/sap/sync/outstanding",
    template: "/templates/outstanding_template.csv",
    title: "Dealer Outstanding Report",
    icon: Landmark,
    hint: "SAP A/R Aging / Balance export: BP Code, BP Name, Total Invoiced, Amount Paid, Outstanding Balance, Last PO Number. Uploads real-time SAP customer ledger balances directly into Supabase.",
  },
];

export default function SapSync() {
  const [status, setStatus] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [busy, setBusy] = useState({});
  const [lastResult, setLastResult] = useState({});

  const load = () => {
    api.get("/sap/status").then(({ data }) => setStatus(data));
    api.get("/sap/price-coverage").then(({ data }) => setCoverage(data)).catch(() => setCoverage(null));
  };
  useEffect(() => { load(); }, []);

  const upload = async (kind) => {
    const inp = document.getElementById(`file-${kind.key}`);
    const f = inp?.files?.[0];
    if (!f) return toast.error("Pick a CSV first");
    setBusy((b) => ({ ...b, [kind.key]: true }));
    try {
      const fd = new FormData();
      fd.append("file", f);
      // FormData needs raw fetch (axios also works, but keep it simple)
      const token = getToken();
      const r = await fetch(`${API_BASE}${kind.endpoint}`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
        body: fd,
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setLastResult((s) => ({ ...s, [kind.key]: data }));
      toast.success(`Sync complete — ${JSON.stringify(data)}`);
      load();    } catch (e) {
      toast.error(`Sync failed: ${e.message}`);
    } finally {
      setBusy((b) => ({ ...b, [kind.key]: false }));
      if (inp) inp.value = "";
    }
  };

  const [sapConfig, setSapConfig] = useState({
    server_url: "https://NAPLES.B1CLOUD.IN:50000/b1s/v1",
    company_db: "LIVE_BSE_ECH_MRG",
    username: "b1cloud\\bhawar.indus",
    password: "",
  });
  const [testingSap, setTestingSap] = useState(false);
  const [sapStatusMsg, setSapStatusMsg] = useState(null);

  const testSapConnection = async () => {
    setTestingSap(true);
    setSapStatusMsg(null);
    try {
      const { data } = await api.post("/sap/test-connection", sapConfig);
      if (data.ok) {
        toast.success("SAP B1 Live Service Layer Connected!");
        setSapStatusMsg({ success: true, text: `Connected! Session ID: ${data.session?.SessionId || "Active"}` });
      } else {
        toast.error("SAP Connection failed");
        setSapStatusMsg({ success: false, text: data.detail || "Authentication failed" });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed to reach SAP Service Layer");
      setSapStatusMsg({ success: false, text: e.response?.data?.detail || e.message });
    } finally {
      setTestingSap(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="sap-sync-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">SAP B1 Enterprise Integration</div>
        <h1 className="mt-1 text-3xl font-medium tracking-tight" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>SAP Business One Sync Hub</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Live Service Layer connection and CSV master data pipelines. Data is synchronized directly with Supabase for real-time B2B orders, multi-tier pricing, and Ind AS customer ledgers.
        </p>
      </div>

      {/* Live SAP B1 Connection & Settings Card */}
      <Card className="p-5 border-slate-200 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-700 pb-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-600/30 text-blue-400 rounded-lg border border-blue-500/30">
              <Database size={22} />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">SAP B1 Service Layer Connection</div>
              <div className="text-xs text-slate-400">Live HANA database endpoint for automatic sync of stock, invoices, and ledgers</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={testSapConnection}
              disabled={testingSap}
              className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold px-4"
            >
              <RefreshCw size={13} className={`mr-2 ${testingSap ? "animate-spin" : ""}`} />
              {testingSap ? "Testing Connection..." : "Test Connection"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
          <div>
            <label className="text-slate-400 block mb-1 text-[11px] font-medium">Service Layer URL</label>
            <input
              type="text"
              value={sapConfig.server_url}
              onChange={(e) => setSapConfig(prev => ({ ...prev, server_url: e.target.value }))}
              className="w-full bg-slate-950/80 border border-slate-700 rounded px-2.5 py-1.5 text-slate-200 font-mono text-xs focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="text-slate-400 block mb-1 text-[11px] font-medium">Company DB</label>
            <input
              type="text"
              value={sapConfig.company_db}
              onChange={(e) => setSapConfig(prev => ({ ...prev, company_db: e.target.value }))}
              className="w-full bg-slate-950/80 border border-slate-700 rounded px-2.5 py-1.5 text-slate-200 font-mono text-xs focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="text-slate-400 block mb-1 text-[11px] font-medium">SAP Username</label>
            <input
              type="text"
              value={sapConfig.username}
              onChange={(e) => setSapConfig(prev => ({ ...prev, username: e.target.value }))}
              className="w-full bg-slate-950/80 border border-slate-700 rounded px-2.5 py-1.5 text-slate-200 font-mono text-xs focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="text-slate-400 block mb-1 text-[11px] font-medium">SAP Password</label>
            <input
              type="password"
              placeholder="••••••••"
              value={sapConfig.password}
              onChange={(e) => setSapConfig(prev => ({ ...prev, password: e.target.value }))}
              className="w-full bg-slate-950/80 border border-slate-700 rounded px-2.5 py-1.5 text-slate-200 font-mono text-xs focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>

        {sapStatusMsg && (
          <div className={`mt-3 p-2.5 rounded text-xs flex items-center gap-2 ${sapStatusMsg.success ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" : "bg-rose-500/20 text-rose-300 border border-rose-500/30"}`}>
            {sapStatusMsg.success ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
            <span>{sapStatusMsg.text}</span>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {KINDS.map((k) => {
          const Icon = k.icon;
          const running = busy[k.key];
          const res = lastResult[k.key];
          return (
            <Card key={k.key} className="p-5" data-testid={`sap-card-${k.key}`}>
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-md bg-blue-50 text-blue-700"><Icon size={18} /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{k.title}</div>
                  <div className="text-xs text-slate-500 mt-1 leading-snug">{k.hint}</div>
                </div>
              </div>
              <div className="mt-4">
                <input
                  id={`file-${k.key}`}
                  type="file"
                  accept=".csv,text/csv"
                  className="text-xs w-full file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-slate-900 file:text-white file:text-xs hover:file:bg-slate-800"
                  data-testid={`sap-file-${k.key}`}
                />
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Button
                  onClick={() => upload(k)}
                  disabled={running || !status?.supabase_online}
                  className="flex-1 bg-[#0055FF] hover:bg-[#0044CC]"
                  data-testid={`sap-upload-${k.key}`}
                >
                  {running ? "Uploading..." : (<><Upload size={14} className="mr-2" />Upload & sync</>)}
                </Button>
                <a
                  href={k.template}
                  download
                  className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-slate-200 text-xs text-slate-700 hover:bg-slate-50 transition-colors whitespace-nowrap"
                  data-testid={`sap-template-${k.key}`}
                  title="Download CSV template"
                >
                  <Download size={13} /> Template
                </a>
              </div>
              {res && (
                <div className="mt-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2 font-mono break-all">
                  {JSON.stringify(res)}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <Card className="p-5">
        <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-3">Price list coverage</div>
        {!coverage && <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>}
        {coverage && (
          <>
            {coverage.dealer_price_map_rows === 0 && (
              <div className="mb-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3">
                <span className="font-medium">dealer_price_map is empty</span> — no dealer has a price
                group assigned, so nobody will get a price on the Purchase Order page. Re-upload the
                Dealers &amp; Addresses CSV (or the Dealer Group Map CSV) with a populated
                <code className="mx-1 px-1 py-0.5 bg-rose-100 rounded text-xs">Group Name</code>
                column — it must match a price list's Group Name exactly.
              </div>
            )}
            {coverage.dealers_without_price_group?.length > 0 && (
              <div className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
                {coverage.dealers_without_price_group.length} active dealer(s) have no price group mapped:{" "}
                {coverage.dealers_without_price_group.slice(0, 8).map(d => d.name || d.code).join(", ")}
                {coverage.dealers_without_price_group.length > 8 ? "…" : ""}
              </div>
            )}
            <div className="divide-y divide-slate-100">
              {coverage.price_lists?.map((pl) => (
                <div key={pl.group_name} className="py-2 flex items-center justify-between text-sm">
                  <span className="font-medium">{pl.group_name}</span>
                  <span className="text-xs text-slate-500">{pl.sku_count} SKUs</span>
                  <Badge variant="outline" className={pl.dealer_count === 0 ? "text-rose-700 border-rose-200 bg-rose-50" : "text-emerald-700 border-emerald-200 bg-emerald-50"}>
                    {pl.dealer_count === 0 ? "No dealers mapped" : `${pl.dealer_count} dealer(s)`}
                  </Badge>
                </div>
              ))}
              {coverage.price_lists?.length === 0 && (
                <div className="text-sm text-slate-500 py-4 text-center">No price lists synced yet.</div>
              )}
            </div>
          </>
        )}
      </Card>

      <Card className="p-5">
        <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-3">Recent syncs</div>
        {(!status || status.recent_syncs?.length === 0) && (
          <div className="text-sm text-slate-500 py-6 text-center">No syncs recorded yet.</div>
        )}
        <div className="divide-y divide-slate-100">
          {status?.recent_syncs?.map((row) => (
            <div key={row.id} className="py-2.5 flex items-center gap-3 text-sm">
              {row.rows_failed > 0 ? (
                <AlertTriangle size={14} className="text-amber-700 shrink-0" />
              ) : (
                <CheckCircle2 size={14} className="text-emerald-700 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-slate-800">
                  <span className="capitalize font-medium">{row.kind}</span>
                  <span className="text-slate-500"> · {row.rows_upserted} rows</span>
                  {row.notes && <span className="text-xs text-slate-500 ml-2">— {row.notes}</span>}
                </div>
                <div className="text-[11px] text-slate-500">{row.actor_email} · {new Date(row.created_at).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

    </div>
  );
}
